import { BitkitEmptyState, BitkitLink, IconApp } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';

import useArtifacts from '@/hooks/api/useArtifacts';

import GoToInstallableArtifactDetailsAlert from './components/GoToInstallableArtifactDetailsAlert';
import HtmlReportsTable from './components/HtmlReportsTable';
import InstallableArtifactsTable from './components/InstallableArtifactsTable';
import OtherArtifactsTable from './components/OtherArtifactsTable';
import useHtmlReports from './hooks/useHtmlReports';

type ArtifactListProps = {
  appSlug: string;
  buildSlug?: string;
  triggeredWorkflow?: string;
  pipelineSlug?: string;
  attemptId?: string;
  isRunning: boolean;
};

const PersistArtifactsInfo = () => {
  return (
    <>
      Files generated during a build are destroyed by default.
      <br />
      If you want a file to persist, you can{' '}
      <BitkitLink
        colorVariant="purple"
        href="https://bitrise.io/integrations/steps/deploy-to-bitrise-io"
        textStyle="body/md/regular"
      >
        deploy them as an artifact using the “Deploy to Bitrise.io” Step
      </BitkitLink>
    </>
  );
};

const ArtifactList = ({
  appSlug,
  buildSlug,
  pipelineSlug,
  triggeredWorkflow,
  attemptId,
  isRunning,
}: ArtifactListProps) => {
  const {
    artifacts,
    installableArtifacts,
    isFetched: isArtifactsFetched,
    otherArtifacts,
    intermediateFiles,
  } = useArtifacts({
    appSlug,
    pipelineSlug,
    attemptId,
    buildSlug,
    enabled: !isRunning,
  });

  const { htmlReports, isFetched: isHtmlReportsFetched } = useHtmlReports({
    appSlug,
    pipelineSlug,
    attemptId,
    buildSlug,
    options: { enabled: !isRunning },
  });

  if (isRunning || !isArtifactsFetched || !isHtmlReportsFetched) {
    return (
      <BitkitEmptyState
        bodyText="Your artifacts will appear here."
        icon={IconApp}
        loading
        headingText="Waiting for artifacts"
      />
    );
  }

  if (!artifacts.length && !htmlReports.length) {
    return (
      <BitkitEmptyState
        bodyText={<PersistArtifactsInfo />}
        icon={IconApp}
        headingText="This build generated no artifacts."
      />
    );
  }

  const isTableCaptionsVisible =
    [installableArtifacts.length, otherArtifacts.length, intermediateFiles.length, htmlReports.length].filter(Boolean)
      .length > 1;

  return (
    <Box display="flex" flexDir="column" rowGap={32}>
      <GoToInstallableArtifactDetailsAlert hasInstallableArtifacts={installableArtifacts.length > 0} />

      <InstallableArtifactsTable
        appSlug={appSlug}
        buildSlug={buildSlug}
        triggeredWorkflow={triggeredWorkflow}
        artifacts={installableArtifacts}
        isTableCaptionVisible={isTableCaptionsVisible}
      />

      <HtmlReportsTable
        htmlReports={htmlReports}
        isTableCaptionVisible={isTableCaptionsVisible}
        trackingParams={{
          appSlug,
          pipelineSlug,
          tabName: 'artifacts',
        }}
      />

      <OtherArtifactsTable
        caption="Artifacts"
        appSlug={appSlug}
        buildSlug={buildSlug}
        triggeredWorkflow={triggeredWorkflow}
        artifacts={otherArtifacts}
        isTableCaptionVisible={isTableCaptionsVisible}
      />

      <OtherArtifactsTable
        caption="Intermediate files"
        appSlug={appSlug}
        buildSlug={buildSlug}
        triggeredWorkflow={triggeredWorkflow}
        artifacts={intermediateFiles}
        isTableCaptionVisible={isTableCaptionsVisible}
      />
    </Box>
  );
};

export default ArtifactList;
